/**
 * Core timestamp and date conversion utilities
 * Universal format: ISO strings (YYYY-MM-DDTHH:mm:ss.sssZ)
 * All dates in the system should use ISO strings for consistency
 */

/**
 * Creates a standardized timestamp in ISO format
 * This is the universal timestamp format for the entire system
 */
export const createTimestamp = (): string => {
  return new Date().toISOString();
};

/**
 * Converts Firestore Timestamp to standardized ISO string
 * Handles both Firestore admin and client SDK timestamps
 */
export const firestoreToISO = (timestamp: any): string => {
  if (!timestamp) return createTimestamp();
  
  // Handle Firestore admin SDK timestamp
  if (timestamp._seconds && timestamp._nanoseconds !== undefined) {
    return new Date(timestamp._seconds * 1000 + timestamp._nanoseconds / 1000000).toISOString();
  }
  
  // Handle Firestore client SDK timestamp with toDate() method
  if (typeof timestamp.toDate === 'function') {
    return timestamp.toDate().toISOString();
  }
  
  // Handle already converted Date object
  if (timestamp instanceof Date) {
    return timestamp.toISOString();
  }
  
  // Handle ISO string (already in correct format)
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    if (!isNaN(date.getTime())) {
      return date.toISOString();
    }
  }
  
  // Fallback to current timestamp
 
  return createTimestamp();
};

/**
 * Safely parses any date input to a Date object
 * Handles all common date formats in the system
 */
export const safeParseDate = (input: any): Date => {
  if (!input) return new Date();
  
  // Handle Date objects
  if (input instanceof Date) {
    return isNaN(input.getTime()) ? new Date() : input;
  }
  
  // Handle Firestore timestamps
  if (input._seconds !== undefined || typeof input.toDate === 'function') {
    const isoString = firestoreToISO(input);
    return new Date(isoString);
  }
  
  // Handle strings and numbers
  if (typeof input === 'string' || typeof input === 'number') {
    const date = new Date(input);
    return isNaN(date.getTime()) ? new Date() : date;
  }
  
  // Fallback
 
  return new Date();
};

/**
 * Converts any date input to standardized ISO string format
 */
export const toStandardDate = (date: any): string => {
  return safeParseDate(date).toISOString();
};

/**
 * Standardizes date fields in an object
 */
export const standardizeDateFields = (obj: any, dateFields: string[]): any => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const result = { ...obj };
  dateFields.forEach(field => {
    if (result[field]) {
      result[field] = toStandardDate(result[field]);
    }
  });
  
  return result;
};

/**
 * Standardizes common timestamp fields (createdAt, updatedAt)
 */
export const standardizeTimestamps = (data: any): any => {
  return standardizeDateFields(data, ['createdAt', 'updatedAt', 'timestamp', 'date']);
};

/**
 * Gets a date value from an object with fallback
 */
export const getDateValue = (obj: any, field: string, fallback?: string): string => {
  if (!obj) return fallback || createTimestamp();
  
  const value = obj[field];
  return value ? toStandardDate(value) : (fallback || createTimestamp());
};
